import {
  useCallback,
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { useState } from "@vrf/fr-vrf-core-react";
import { InterfaceSpreadsheetRow } from "../../../pages/interface-definitions";
import { ReviewDiffCellDetail } from "../../if-definitions";

const TOOLTIP_OFFSET_X = 12;
const TOOLTIP_OFFSET_Y = 10;
const TOOLTIP_MAX_WIDTH = 320;
const TOOLTIP_MAX_HEIGHT = 220;
const VIEWPORT_MARGIN = 8;
const CLOSE_DELAY = 240;

const IF_LIST_MODIFIED_DIFF_HIDDEN_PATHS: Set<string> = new Set([
  "src.interfaceDefinition.dipByRegion",
  "src.interfaceDefinition.interfaceRequirementType",
  "src.interfaceDefinition.inbound.inboundId",

  "dst.interfaceDefinition.dipByRegion",
  "dst.interfaceDefinition.interfaceRequirementType",
  "dst.interfaceDefinition.outbound.outboundId",

  "src.interfaceDefinition.interfaceNameEnglish",
  "src.interfaceDefinition.interfaceNameJapanese",
]);

export type HoverDiffTooltipState = {
  visible: boolean;
  diff?: ReviewDiffCellDetail;
  x: number;
  y: number;
};

type MousePosition = {
  clientX: number;
  clientY: number;
};

type SpreadsheetEventDetail = {
  name?: string;
  value?: InterfaceSpreadsheetRow;
  nativeEvent?: unknown;
  event?: unknown;
  originalEvent?: unknown;
};

type UseHoverDiffTooltipParams = {
  resolveDiff: (
    row: InterfaceSpreadsheetRow | undefined,
    diffPath: string | undefined,
  ) => ReviewDiffCellDetail | undefined;
  enabled?: boolean;
  resetKey?: unknown;
};

type SelectionState = "none" | "selecting" | "pinned";

type TooltipRuntimeState = {
  activeTargetKey?: string;
  hovered: boolean;
  selection: SelectionState;
};

const INITIAL_TOOLTIP_STATE: HoverDiffTooltipState = {
  visible: false,
  diff: undefined,
  x: 0,
  y: 0,
};

const createRuntimeState = (): TooltipRuntimeState => ({
  activeTargetKey: undefined,
  hovered: false,
  selection: "none",
});

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object";

const toStableKeyPart = (value: unknown): string | undefined => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (!isObject(value)) {
    return undefined;
  }

  const wrappedValue = value.value;

  if (typeof wrappedValue === "string" || typeof wrappedValue === "number") {
    return String(wrappedValue);
  }

  const initValue = value.initValue;

  if (typeof initValue === "string" || typeof initValue === "number") {
    return String(initValue);
  }

  return undefined;
};

const createTooltipTargetKey = (
  detail: SpreadsheetEventDetail,
): string | undefined => {
  const diffPath = detail.name;

  if (!diffPath) {
    return undefined;
  }

  const row = detail.value;

  const rowKey =
    toStableKeyPart(row?.project?.ifReqId) ?? toStableKeyPart(row?.idx);

  if (rowKey === undefined) {
    return undefined;
  }

  return `${rowKey}:${diffPath}`;
};

const isMousePosition = (value: unknown): value is MousePosition => {
  return (
    isObject(value) &&
    typeof value.clientX === "number" &&
    typeof value.clientY === "number"
  );
};

const getMousePosition = (value: unknown): MousePosition | undefined => {
  if (isMousePosition(value)) {
    return value;
  }

  if (isObject(value) && isMousePosition(value.nativeEvent)) {
    return value.nativeEvent;
  }

  return undefined;
};

const resolveMousePosition = (
  detail: SpreadsheetEventDetail,
): MousePosition | undefined => {
  return (
    getMousePosition(detail.nativeEvent) ??
    getMousePosition(detail.event) ??
    getMousePosition(detail.originalEvent)
  );
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const calcAxisPosition = (
  pointerPosition: number,
  viewportSize: number,
  tooltipSize: number,
  offset: number,
): number => {
  const min = VIEWPORT_MARGIN;

  const max = Math.max(min, viewportSize - tooltipSize - VIEWPORT_MARGIN);

  const canPlaceAfter =
    pointerPosition + offset + tooltipSize <= viewportSize - VIEWPORT_MARGIN;

  const preferredPosition = canPlaceAfter
    ? pointerPosition + offset
    : pointerPosition - tooltipSize - offset;

  return clamp(preferredPosition, min, max);
};

const calcTooltipPosition = (clientX: number, clientY: number) => ({
  x: calcAxisPosition(
    clientX,
    window.innerWidth,
    TOOLTIP_MAX_WIDTH,
    TOOLTIP_OFFSET_X,
  ),
  y: calcAxisPosition(
    clientY,
    window.innerHeight,
    TOOLTIP_MAX_HEIGHT,
    TOOLTIP_OFFSET_Y,
  ),
});

const hasActiveTextSelection = (): boolean => {
  const selection = window.getSelection?.();

  return !!selection && !selection.isCollapsed && selection.toString() !== "";
};

export const useHoverDiffTooltip = ({
  resolveDiff,
  enabled = true,
  resetKey,
}: UseHoverDiffTooltipParams) => {
  const closeTimerRef = useRef<number | null>(null);
  const runtimeRef = useRef<TooltipRuntimeState>(createRuntimeState());

  const [hoverTooltip, setHoverTooltip] = useState<HoverDiffTooltipState>(
    INITIAL_TOOLTIP_STATE,
  );

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current === null) {
      return;
    }

    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const closeTooltip = useCallback(() => {
    clearCloseTimer();

    runtimeRef.current = createRuntimeState();

    setHoverTooltip((current) => {
      if (!current.visible) {
        return current;
      }

      return INITIAL_TOOLTIP_STATE;
    });
  }, [clearCloseTimer]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();

    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;

      const { hovered, selection } = runtimeRef.current;

      if (!hovered && selection === "none") {
        closeTooltip();
      }
    }, CLOSE_DELAY);
  }, [clearCloseTimer, closeTooltip]);

  useEffect(() => {
    if (!enabled) {
      closeTooltip();
    }
  }, [enabled, closeTooltip]);

  useEffect(() => {
    closeTooltip();
  }, [resetKey, closeTooltip]);

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!hoverTooltip.visible) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeTooltip();
      }
    };

    const handleMouseUp = () => {
      const runtime = runtimeRef.current;

      if (runtime.selection !== "selecting") {
        return;
      }

      if (hasActiveTextSelection()) {
        runtime.selection = "pinned";
        clearCloseTimer();
        return;
      }

      runtime.selection = "none";

      if (!runtime.hovered) {
        scheduleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);

      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [hoverTooltip.visible, clearCloseTimer, closeTooltip, scheduleClose]);

  const handleHover = useCallback(
    (event: CustomEvent<SpreadsheetEventDetail | undefined>) => {
      if (!enabled) {
        closeTooltip();
        return;
      }

      const runtime = runtimeRef.current;

      if (runtime.selection === "pinned") {
        return;
      }

      if (runtime.hovered || runtime.selection === "selecting") {
        clearCloseTimer();
        return;
      }

      const detail = event.detail ?? {};
      const diffPath = detail.name;
      const row = detail.value;

      if (diffPath && IF_LIST_MODIFIED_DIFF_HIDDEN_PATHS.has(diffPath)) {
        const side = diffPath.startsWith("src.")
          ? "src"
          : diffPath.startsWith("dst.")
            ? "dst"
            : undefined;

        if (side && row?.reviewDiffInfo?.[side]?.changeType === "modified") {
          scheduleClose();
          return;
        }
      }

      const diff = resolveDiff(row, diffPath);

      if (!diff) {
        scheduleClose();
        return;
      }

      const targetKey = createTooltipTargetKey(detail);

      if (!targetKey) {
        scheduleClose();
        return;
      }

      if (runtime.activeTargetKey === targetKey) {
        clearCloseTimer();
        return;
      }

      const mousePosition = resolveMousePosition(detail);

      if (!mousePosition) {
        scheduleClose();
        return;
      }

      clearCloseTimer();

      runtimeRef.current = {
        activeTargetKey: targetKey,
        hovered: false,
        selection: "none",
      };

      setHoverTooltip({
        visible: true,
        diff,
        ...calcTooltipPosition(mousePosition.clientX, mousePosition.clientY),
      });
    },
    [enabled, resolveDiff, clearCloseTimer, closeTooltip, scheduleClose],
  );

  const handleHoverAreaMouseEnter = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  const handleHoverAreaMouseLeave = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      const relatedTarget = event.relatedTarget;

      if (
        relatedTarget instanceof Node &&
        event.currentTarget.contains(relatedTarget)
      ) {
        return;
      }

      scheduleClose();
    },
    [scheduleClose],
  );

  const handleTooltipMouseEnter = useCallback(() => {
    runtimeRef.current.hovered = true;
    clearCloseTimer();
  }, [clearCloseTimer]);

  const handleTooltipMouseLeave = useCallback(() => {
    const runtime = runtimeRef.current;

    runtime.hovered = false;

    if (runtime.selection === "none") {
      scheduleClose();
    }
  }, [scheduleClose]);

  const handleTooltipMouseDown = useCallback(() => {
    const runtime = runtimeRef.current;

    runtime.hovered = true;
    runtime.selection = "selecting";

    clearCloseTimer();
  }, [clearCloseTimer]);

  return {
    handleHover,
    handleHoverAreaMouseEnter,
    handleHoverAreaMouseLeave,
    closeTooltip,
    tooltipProps: {
      visible: hoverTooltip.visible,
      diff: hoverTooltip.diff,
      x: hoverTooltip.x,
      y: hoverTooltip.y,
      maxWidth: TOOLTIP_MAX_WIDTH,
      maxHeight: TOOLTIP_MAX_HEIGHT,
      onMouseEnter: handleTooltipMouseEnter,
      onMouseLeave: handleTooltipMouseLeave,
      onMouseDown: handleTooltipMouseDown,
    },
  };
};
